import React, { useEffect, useState, useRef } from "react"
import "./EditCommonCourse.scss"
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { InputText } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { useParams, useNavigate } from "react-router-dom"
import CommonCourseApi from "Api/CommonCourseApi"
import { Toast } from 'primereact/toast'

const EditCommonCourse = () => {
  document.title = "Edit Common Course"
  const toast = useRef(null)
  const accessToken = localStorage.getItem("accessToken")
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const [courseData, setCourseData] = useState({
    courseName: "",
    courseOverview: {
      overview: "",
      courseHighlights: ""
    },
    courseCareerOutcome: {
      careerOutcome: "",
      jobRoles: ""
    },
    courseRequirements: {
      eligibility: "",
      medicalAdmission: "",
      admissionRequirements: ""
    },
    courseExam: [],
    faq: []
  })

  const [currentFaq, setCurrentFaq] = useState({
    question: "",
    answer: ""
  });

  const [currentExam, setCurrentExam] = useState({
    examName: "",
    examDate: "",
    examDuration: "",
    examLocation: "",
    examRegistration: "",
    examFee: "",
    examDescription: ""
  });

  const [editingExamIndex, setEditingExamIndex] = useState(null);
  const [editingFaqIndex, setEditingFaqIndex] = useState(null);
  const [showExamForm, setShowExamForm] = useState(false);
  const [showFaqForm, setShowFaqForm] = useState(false);

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "Common Courses", link: "#" },
    { label: "Edit Common Course", link: "#" },
  ]

  const getCommonCourse = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    CommonCourseApi.getCommonCourseById(id, config)
      .then(res => {
        if (res.data.status) {
          setCourseData(res.data.data)
        } else {
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: res.data.message || 'Failed to fetch common course details',
            life: 3000
          })
        }
      })
      .catch(error => {
        console.error("Error fetching common course:", error)
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: error.response?.data?.message || 'Failed to fetch common course details',
          life: 3000
        })
      })
  }

  const handleInputChange = (e, section, field) => {
    if (section) {
      setCourseData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: e.target.value
        }
      }))
    } else {
      setCourseData(prev => ({
        ...prev,
        [field]: e.target.value
      }))
    }
  }

  const handleExamChange = (field, value) => {
    setCurrentExam(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFaqChange = (field, value, index = null) => {
    if (index !== null) {
      const updatedFaqs = [...courseData.faq];
      updatedFaqs[index] = {
        ...updatedFaqs[index],
        [field]: value
      };
      setCourseData(prev => ({
        ...prev,
        faq: updatedFaqs
      }));
    } else {
      setCurrentFaq(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const addFaq = () => {
    if (!currentFaq.question || !currentFaq.answer) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill both question and answer fields',
        life: 3000
      });
      return;
    }

    setCourseData(prev => ({
      ...prev,
      faq: [...prev.faq, { ...currentFaq }]
    }));

    // Reset the current FAQ form
    setCurrentFaq({
      question: "",
      answer: ""
    });

    toast.current.show({
      severity: 'success',
      summary: 'Success',
      detail: 'FAQ added successfully',
      life: 3000
    });
  };

  const removeFaq = (index) => {
    setCourseData(prev => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    CommonCourseApi.updateCommonCourse(id, courseData, config)
      .then(res => {
        if (res.data.status) {
          toast.current.show({
            severity: 'success',
            summary: 'Success',
            detail: res.data.message,
            life: 3000
          })
          setTimeout(() => {
            navigate("/commonCourseList")
          }, 1000)
        } else {
          setTimeout(() => {
            toast.current.show({
              severity: 'error',
              summary: 'Error',
              detail: res.data.message || 'Failed to update common course',
              life: 3000
            })
          }, 1000)
        }
        setLoading(false)
      })
      .catch(error => {
        console.error("Error updating common course:", error)
        setTimeout(() => {
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to update common course',
            life: 3000
          })
        }, 1000)
        setLoading(false)
      })
  }

  const saveExam = () => {
    if (!currentExam.examName || !currentExam.examDate) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill required fields',
        life: 3000
      });
      return;
    }

    if (editingExamIndex !== null) {
      // Update existing exam
      setCourseData(prev => ({
        ...prev,
        courseExam: prev.courseExam.map((exam, index) => 
          index === editingExamIndex ? currentExam : exam
        )
      }));
      setEditingExamIndex(null);
    } else {
      // Add new exam
      setCourseData(prev => ({
        ...prev,
        courseExam: [...prev.courseExam, currentExam]
      }));
    }

    // Reset form
    setCurrentExam({
      examName: "",
      examDate: "",
      examDuration: "",
      examLocation: "",
      examRegistration: "",
      examFee: "",
      examDescription: ""
    });
    setShowExamForm(false);
  };

  const startEditingExam = (index) => {
    setCurrentExam(courseData.courseExam[index]);
    setEditingExamIndex(index);
    setShowExamForm(true);
  };

  const cancelExamEdit = () => {
    setCurrentExam({
      examName: "",
      examDate: "",
      examDuration: "",
      examLocation: "",
      examRegistration: "",
      examFee: "",
      examDescription: ""
    });
    setEditingExamIndex(null);
    setShowExamForm(false);
  };

  useEffect(() => {
    if (id) {
      getCommonCourse()
    }
  }, [id])

  return (
    <div className="edit-common-course">
      <Toast ref={toast} />
      <Row>
        <Col xs="12">
          <div className="common-course-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb data={breadcrumbItems} pageTitle="Edit Common Course" />
          </div>

          <Card className="mt-4">
            <CardBody>
              <form onSubmit={handleSubmit} className="common-course-edit-form">
                <div className="course-edit-inp">
                  <label htmlFor="courseName">Course Name</label>
                  <div className="inp-err">
                    <InputText
                      id="courseName"
                      value={courseData.courseName}
                      onChange={(e) => handleInputChange(e, null, 'courseName')}
                      className="inp-field-box"
                      required
                    />
                  </div>
                </div>

                <div className="course-edit-inp">
                  <label htmlFor="overview">Overview</label>
                  <div className="inp-err">
                    <InputTextarea
                      id="overview"
                      value={courseData.courseOverview?.overview}
                      onChange={(e) => handleInputChange(e, 'courseOverview', 'overview')}
                      className="inp-field-box"
                      rows={3}
                      required
                    />
                  </div>
                </div>

                <div className="course-edit-inp">
                  <label htmlFor="courseHighlights">Course Highlights</label>
                  <div className="inp-err">
                    <InputTextarea
                      id="courseHighlights"
                      value={courseData.courseOverview?.courseHighlights}
                      onChange={(e) => handleInputChange(e, 'courseOverview', 'courseHighlights')}
                      className="inp-field-box"
                      rows={3}
                      required
                    />
                  </div>
                </div>

                <div className="course-edit-inp">
                  <label htmlFor="careerOutcome">Career Outcome</label>
                  <div className="inp-err">
                    <InputTextarea
                      id="careerOutcome"
                      value={courseData.courseCareerOutcome?.careerOutcome}
                      onChange={(e) => handleInputChange(e, 'courseCareerOutcome', 'careerOutcome')}
                      className="inp-field-box"
                      rows={3}
                      required
                    />
                  </div>
                </div>

                <div className="course-edit-inp">
                  <label htmlFor="jobRoles">Job Roles</label>
                  <div className="inp-err">
                    <InputTextarea
                      id="jobRoles"
                      value={courseData.courseCareerOutcome?.jobRoles}
                      onChange={(e) => handleInputChange(e, 'courseCareerOutcome', 'jobRoles')}
                      className="inp-field-box"
                      rows={3}
                      required
                    />
                  </div>
                </div>

                <div className="course-edit-inp">
                  <label htmlFor="eligibility">Eligibility</label>
                  <div className="inp-err">
                    <InputTextarea
                      id="eligibility"
                      value={courseData.courseRequirements?.eligibility}
                      onChange={(e) => handleInputChange(e, 'courseRequirements', 'eligibility')}
                      className="inp-field-box"
                      rows={3}
                      required
                    />
                  </div>
                </div>

                <div className="course-edit-inp">
                  <label htmlFor="medicalAdmission">Medical Admission</label>
                  <div className="inp-err">
                    <InputTextarea
                      id="medicalAdmission"
                      value={courseData.courseRequirements?.medicalAdmission}
                      onChange={(e) => handleInputChange(e, 'courseRequirements', 'medicalAdmission')}
                      className="inp-field-box"
                      rows={3}
                      required
                    />
                  </div>
                </div>

                <div className="course-edit-inp">
                  <label htmlFor="admissionRequirements">Admission Requirements</label>
                  <div className="inp-err">
                    <InputTextarea
                      id="admissionRequirements"
                      value={courseData.courseRequirements?.admissionRequirements}
                      onChange={(e) => handleInputChange(e, 'courseRequirements', 'admissionRequirements')}
                      className="inp-field-box"
                      rows={3}
                      required
                    />
                  </div>
                </div>

                {/* Exams Section */}
                <div className="course-edit-inp">
                  <label>Exams</label>
                  <div className="exams-section">
                    {showExamForm ? (
                      <div className="exams-input-form">
                        <div className="mb-2">
                          <label>Exam Name</label>
                          <div className="inp-err">
                            <InputText
                              value={currentExam.examName}
                              onChange={(e) => handleExamChange('examName', e.target.value)}
                              placeholder="Enter Exam Name"
                              className="p-inputtext"
                            />
                          </div>
                        </div>

                        <div className="mb-2">
                          <label>Exam Date</label>
                          <div className="inp-err">
                            <InputText
                              type="date"
                              value={currentExam.examDate}
                              onChange={(e) => handleExamChange('examDate', e.target.value)}
                              placeholder="Select Exam Date"
                              className="p-inputtext"
                            />
                          </div>
                        </div>

                        <div className="mb-2">
                          <label>Duration</label>
                          <div className="inp-err">
                            <InputText
                              value={currentExam.examDuration}
                              onChange={(e) => handleExamChange('examDuration', e.target.value)}
                              placeholder="Enter Duration"
                              className="p-inputtext"
                            />
                          </div>
                        </div>

                        <div className="mb-2">
                          <label>Location</label>
                          <div className="inp-err">
                            <InputText
                              value={currentExam.examLocation}
                              onChange={(e) => handleExamChange('examLocation', e.target.value)}
                              placeholder="Enter Location"
                              className="p-inputtext"
                            />
                          </div>
                        </div>

                        <div className="mb-2">
                          <label>Registration</label>
                          <div className="inp-err">
                            <InputText
                              value={currentExam.examRegistration}
                              onChange={(e) => handleExamChange('examRegistration', e.target.value)}
                              placeholder="Enter Registration"
                              className="p-inputtext"
                            />
                          </div>
                        </div>

                        <div className="mb-2">
                          <label>Fee</label>
                          <div className="inp-err">
                            <InputText
                              value={currentExam.examFee}
                              onChange={(e) => handleExamChange('examFee', e.target.value)}
                              placeholder="Enter Fee"
                              className="p-inputtext"
                            />
                          </div>
                        </div>

                        <div className="mb-2">
                          <label>Description</label>
                          <div className="inp-err">
                            <InputTextarea
                              value={currentExam.examDescription}
                              onChange={(e) => handleExamChange('examDescription', e.target.value)}
                              placeholder="Enter Description"
                              rows={3}
                              className="p-inputtextarea"
                            />
                          </div>
                        </div>

                        <div className="add-exam-btn d-flex gap-2">
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onClick={saveExam}
                          >
                            {editingExamIndex !== null ? 'Update Exam' : 'Save Exam'}
                          </Button>
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            onClick={cancelExamEdit}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="exams-list">
                        <h6>Added Exams</h6>
                        {courseData.courseExam && courseData.courseExam.length > 0 && (
                          courseData.courseExam.map((exam, index) => (
                            <div key={index} className="exam-item">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <strong>Exam Name: {exam.examName}</strong>
                                  <p>Date: {exam.examDate.split('T')[0]}</p>
                                  <p>Duration: {exam.examDuration}</p>
                                  <p>Location: {exam.examLocation}</p>
                                  <p>Registration: {exam.examRegistration}</p>
                                  <p>Fee: {exam.examFee}</p>
                                  <p>Description: {exam.examDescription}</p>
                                </div>
                                <div className="d-flex gap-2">
                                  <Button
                                    type="button"
                                    color="primary"
                                    size="sm"
                                    onClick={() => startEditingExam(index)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    type="button"
                                    color="danger"
                                    size="sm"
                                    onClick={() => {
                                      setCourseData(prev => ({
                                        ...prev,
                                        courseExam: prev.courseExam.filter((_, i) => i !== index)
                                      }))
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    <div className="add-exam-btn mt-3">
                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        onClick={() => {
                          setShowExamForm(true);
                          setCurrentExam({
                            examName: "",
                            examDate: "",
                            examDuration: "",
                            examLocation: "",
                            examRegistration: "",
                            examFee: "",
                            examDescription: ""
                          });
                        }}
                      >
                        Add Exam
                      </Button>
                    </div>
                  </div>
                </div>

                {/* FAQs Section */}
                <div className="course-edit-inp">
                  <label>FAQs</label>
                  <div className="faqs-section">
                    {showFaqForm ? (
                      <div className="faqs-input-form">
                        <div className="mb-2">
                          <label>Question</label>
                          <div className="inp-err">
                            <InputTextarea
                              value={currentFaq.question}
                              onChange={(e) => handleFaqChange('question', e.target.value)}
                              placeholder="Enter Question"
                              rows={2}
                              className="p-inputtextarea"
                            />
                          </div>
                        </div>

                        <div className="mb-2">
                          <label>Answer</label>
                          <div className="inp-err">
                            <InputTextarea
                              value={currentFaq.answer}
                              onChange={(e) => handleFaqChange('answer', e.target.value)}
                              placeholder="Enter Answer"
                              rows={3}
                              className="p-inputtextarea"
                            />
                          </div>
                        </div>

                        <div className="add-faq-btn d-flex gap-2">
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onClick={() => {
                              addFaq();
                              setShowFaqForm(false);
                            }}
                          >
                            Save FAQ
                          </Button>
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            onClick={() => {
                              setShowFaqForm(false);
                              setCurrentFaq({
                                question: "",
                                answer: ""
                              });
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : null}

                    {courseData.faq && courseData.faq.length > 0 && (
                      <div className="faqs-list">
                        <h6>Added FAQs</h6>
                        {courseData.faq.map((faq, index) => (
                          <div key={index} className="faq-item">
                            {editingFaqIndex === index ? (
                              <div className="edit-faq-form">
                                <div className="mb-2">
                                  <label>Question</label>
                                  <InputTextarea
                                    value={faq.question}
                                    onChange={(e) => handleFaqChange('question', e.target.value, index)}
                                    rows={2}
                                    className="p-inputtextarea"
                                  />
                                </div>
                                <div className="mb-2">
                                  <label>Answer</label>
                                  <InputTextarea
                                    value={faq.answer}
                                    onChange={(e) => handleFaqChange('answer', e.target.value, index)}
                                    rows={3}
                                    className="p-inputtextarea"
                                  />
                                </div>
                                <div className="d-flex gap-2">
                                  <Button
                                    type="button"
                                    color="success"
                                    size="sm"
                                    onClick={() => setEditingFaqIndex(null)}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    type="button"
                                    color="danger"
                                    size="sm"
                                    onClick={() => setEditingFaqIndex(index)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <strong>Question: {faq.question}</strong>
                                  <p>Answer: {faq.answer}</p>
                                </div>
                                <div className="d-flex gap-2">
                                  <Button
                                    type="button"
                                    color="primary"
                                    size="sm"
                                    onClick={() => setEditingFaqIndex(index)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    type="button"
                                    color="danger"
                                    size="sm"
                                    onClick={() => removeFaq(index)}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="add-faq-btn mt-3">
                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        onClick={() => {
                          setShowFaqForm(true);
                          setCurrentFaq({
                            question: "",
                            answer: ""
                          });
                        }}
                      >
                        Add FAQ
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="save-cancel-btn mt-4 d-flex align-items-center justify-content-end gap-3">
                  <Button
                    type="button"
                    outline
                    color="danger"
                    className="waves-effect waves-light"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="secondary"
                    className="waves-effect waves-light"
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : "Update"}
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default EditCommonCourse 